/**
 * Object Creation Wizard
 *
 * Multi-step wizard for creating new IDF objects with guided field input.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useIDFStore } from '../stores/idfStore';
import { useIDDStore } from '../stores/iddStore';
import { useVisualObjectEditorStore, useWizardState } from './visualObjectEditorStore';
import { FieldEditor } from './FieldEditors';
import { useIsMobile } from '../hooks';
import { cn } from '../lib/utils';
import type { IDDObjectType, IDDField } from '../idd/types';
import type { IDFField } from '../parser/types';
import type { WizardStep } from './types';
import { validateField } from './validation';
import { getGroupOrder } from './types';

/**
 * Progress indicator for wizard steps
 */
function WizardProgress({
  currentStep,
  isMobile,
}: {
  currentStep: WizardStep;
  isMobile: boolean;
}) {
  const steps: { key: WizardStep; label: string; shortLabel: string }[] = [
    { key: 'class', label: 'Select Type', shortLabel: 'Type' },
    { key: 'required', label: 'Required Fields', shortLabel: 'Required' },
    { key: 'optional', label: 'Optional Fields', shortLabel: 'Optional' },
    { key: 'review', label: 'Review', shortLabel: 'Review' },
  ];

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  // Mobile: simplified compact progress indicator
  if (isMobile) {
    return (
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          {steps.map((step, index) => (
            <div
              key={step.key}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium transition-colors',
                index < currentIndex
                  ? 'bg-blue-600 text-white'
                  : index === currentIndex
                    ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-600'
                    : 'bg-gray-100 text-gray-400'
              )}
            >
              {index < currentIndex ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
              ) : (
                index + 1
              )}
            </div>
          ))}
        </div>
        <span className="text-sm font-medium text-gray-700">
          {steps[currentIndex]?.shortLabel}
        </span>
      </div>
    );
  }

  // Desktop: full progress indicator with labels
  return (
    <div className="flex items-center justify-between px-6 py-4">
      {steps.map((step, index) => (
        <div key={step.key} className="flex items-center">
          <div className="flex items-center">
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                index < currentIndex
                  ? 'bg-blue-600 text-white'
                  : index === currentIndex
                    ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-600'
                    : 'bg-gray-100 text-gray-400'
              )}
            >
              {index < currentIndex ? (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
              ) : (
                index + 1
              )}
            </div>
            <span
              className={cn(
                'ml-2 text-sm',
                index <= currentIndex ? 'text-gray-700' : 'text-gray-400'
              )}
            >
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={cn(
                'mx-4 h-px w-12',
                index < currentIndex ? 'bg-blue-600' : 'bg-gray-200'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Class selection step
 */
function ClassSelectionStep({
  onSelect,
  isMobile,
}: {
  onSelect: (className: string) => void;
  isMobile: boolean;
}) {
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  const schema = useIDDStore((state) => state.schema);

  // Get groups with object types
  const groups = useMemo(() => {
    return [...schema.groups].sort(
      (a, b) => getGroupOrder(a.name) - getGroupOrder(b.name)
    );
  }, [schema.groups]);

  // Get group counts
  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (schema.objectTypes) {
      for (const type of schema.objectTypes.values()) {
        const count = counts.get(type.group) || 0;
        counts.set(type.group, count + 1);
      }
    }
    return counts;
  }, [schema.objectTypes]);

  // Filter object types
  const filteredTypes = useMemo(() => {
    let types = Array.from(schema.objectTypes.values());

    // Filter by group
    if (selectedGroup) {
      types = types.filter((t) => t.group === selectedGroup);
    }

    // Filter by search
    if (search.trim()) {
      const query = search.toLowerCase();
      types = types.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.memo.toLowerCase().includes(query)
      );

      // Sort by relevance: exact match > prefix > word-boundary > substring > memo-only
      return types.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aScore = aName === query
          ? 0
          : aName.startsWith(query)
            ? 1
            : aName.includes(':' + query) || aName.includes(' ' + query)
              ? 2
              : aName.includes(query)
                ? 3
                : 4; // memo-only match
        const bScore = bName === query
          ? 0
          : bName.startsWith(query)
            ? 1
            : bName.includes(':' + query) || bName.includes(' ' + query)
              ? 2
              : bName.includes(query)
                ? 3
                : 4;
        if (aScore !== bScore) return aScore - bScore;
        return a.name.localeCompare(b.name);
      });
    }

    // Sort alphabetically when not searching
    return types.sort((a, b) => a.name.localeCompare(b.name));
  }, [schema.objectTypes, selectedGroup, search]);

  // Total count for "All Categories"
  const totalCount = useMemo(() => {
    return schema.objectTypes ? schema.objectTypes.size : 0;
  }, [schema.objectTypes]);

  // Mobile layout with dropdown for categories
  if (isMobile) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Search and Category filter */}
        <div className="border-b p-3 space-y-2">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search object types..."
              className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-4 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
            <svg
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>

          {/* Category dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
              className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2.5 text-left text-sm"
            >
              <span className="text-gray-700">
                {selectedGroup || 'All Categories'}
              </span>
              <svg
                className={cn(
                  'h-5 w-5 text-gray-400 transition-transform',
                  isCategoryDropdownOpen && 'rotate-180'
                )}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {isCategoryDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsCategoryDropdownOpen(false)}
                />
                <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                  <button
                    onClick={() => {
                      setSelectedGroup(null);
                      setIsCategoryDropdownOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2.5 text-left text-sm',
                      !selectedGroup
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <span>All Categories</span>
                    <span className="text-xs text-gray-400">{totalCount}</span>
                  </button>
                  {groups.map((group) => (
                    <button
                      key={group.name}
                      onClick={() => {
                        setSelectedGroup(group.name);
                        setIsCategoryDropdownOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between px-3 py-2.5 text-left text-sm',
                        selectedGroup === group.name
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      <span className="truncate pr-2">{group.name}</span>
                      <span className="flex-shrink-0 text-xs text-gray-400">
                        {groupCounts.get(group.name) || 0}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-3">
          {filteredTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No object types found</p>
              <p className="text-sm">Try a different search term or category</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTypes.slice(0, 100).map((type) => (
                <button
                  key={type.name}
                  onClick={() => onSelect(type.name)}
                  className="w-full rounded-md border border-gray-200 bg-white p-4 text-left active:bg-blue-50"
                >
                  <div className="font-medium text-gray-800">{type.name}</div>
                  {type.memo && (
                    <div className="mt-1 line-clamp-2 text-sm text-gray-500">
                      {type.memo}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                    <span>{type.group}</span>
                    <span>•</span>
                    <span>
                      {type.fields.filter((f) => f.required).length} required
                    </span>
                  </div>
                </button>
              ))}
              {filteredTypes.length > 100 && (
                <div className="text-center py-2 text-xs text-gray-400">
                  Showing first 100 of {filteredTypes.length} results
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout with sidebar
  return (
    <div className="flex-1 overflow-hidden flex">
      {/* Group sidebar */}
      <div className="w-56 border-r overflow-y-auto bg-gray-50">
        <div className="p-2">
          <button
            onClick={() => setSelectedGroup(null)}
            className={cn(
              'w-full rounded px-3 py-2 text-left text-sm',
              !selectedGroup
                ? 'bg-blue-100 text-blue-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            All Categories
          </button>
          {groups.map((group) => (
            <button
              key={group.name}
              onClick={() => setSelectedGroup(group.name)}
              className={cn(
                'w-full rounded px-3 py-2 text-left text-sm',
                selectedGroup === group.name
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              <div className="flex justify-between">
                <span className="truncate">{group.name}</span>
                <span className="text-xs text-gray-400">
                  {groupCounts.get(group.name) || 0}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Object type list */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Search */}
        <div className="border-b p-3">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search object types..."
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
            <svg
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-3">
          {filteredTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No object types found</p>
              <p className="text-sm">Try a different search term or category</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredTypes.slice(0, 100).map((type) => (
                <button
                  key={type.name}
                  onClick={() => onSelect(type.name)}
                  className="w-full rounded-md border border-gray-200 bg-white p-3 text-left hover:border-blue-300 hover:bg-blue-50"
                >
                  <div className="font-medium text-gray-800">{type.name}</div>
                  {type.memo && (
                    <div className="mt-1 line-clamp-2 text-xs text-gray-500">
                      {type.memo}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                    <span>{type.group}</span>
                    <span>•</span>
                    <span>
                      {type.fields.filter((f) => f.required).length} required
                      field
                      {type.fields.filter((f) => f.required).length !== 1
                        ? 's'
                        : ''}
                    </span>
                  </div>
                </button>
              ))}
              {filteredTypes.length > 100 && (
                <div className="text-center py-2 text-xs text-gray-400">
                  Showing first 100 of {filteredTypes.length} results
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Fields step (required or optional)
 */
function FieldsStep({
  classInfo,
  fieldValues,
  onFieldChange,
  showRequired,
  isMobile,
}: {
  classInfo: IDDObjectType;
  fieldValues: Record<number, string>;
  onFieldChange: (index: number, value: string) => void;
  showRequired: boolean;
  isMobile: boolean;
}) {
  const document = useIDFStore((state) => state.document);
  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);

  const fields = classInfo.fields.filter((f) =>
    showRequired ? f.required : !f.required
  );

  const getReferencedObjects = useCallback(
    (fieldInfo: IDDField) => {
      if (!fieldInfo.objectList || !document || !isSchemaLoaded) return [];
      const referencedTypes = schema.referenceLists.get(fieldInfo.objectList);
      if (!referencedTypes) return [];
      return document.objects.filter(
        (obj) =>
          referencedTypes.has(obj.className) ||
          referencedTypes.has(obj.className.toLowerCase())
      );
    },
    [document, schema, isSchemaLoaded]
  );

  if (fields.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center px-4">
          <svg
            className="mx-auto h-12 w-12 text-gray-300"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          <p className="mt-2">
            No {showRequired ? 'required' : 'optional'} fields
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto space-y-4',
        isMobile ? 'p-4' : 'p-6'
      )}
    >
      {fields.map((fieldInfo) => {
        const fieldIndex = classInfo.fields.indexOf(fieldInfo);
        const value = fieldValues[fieldIndex] ?? fieldInfo.default ?? '';
        const field: IDFField = { value };
        const validation = validateField(
          field,
          fieldInfo,
          document,
          isSchemaLoaded ? schema : null
        );

        return (
          <FieldEditor
            key={fieldIndex}
            object={{
              id: '',
              className: classInfo.name,
              fields: [],
              leadingComments: [],
            }}
            field={field}
            fieldInfo={fieldInfo}
            fieldIndex={fieldIndex}
            validation={validation}
            referencedObjects={getReferencedObjects(fieldInfo)}
            onChange={(newValue) => onFieldChange(fieldIndex, newValue)}
          />
        );
      })}
    </div>
  );
}

/**
 * Review step showing all configured values
 */
function ReviewStep({
  classInfo,
  fieldValues,
  isMobile,
}: {
  classInfo: IDDObjectType;
  fieldValues: Record<number, string>;
  isMobile: boolean;
}) {
  const objectName = fieldValues[0] || `New ${classInfo.name}`;

  return (
    <div className={cn('flex-1 overflow-y-auto', isMobile ? 'p-4' : 'p-6')}>
      <div className="rounded-lg border bg-gray-50 p-4">
        <h3 className="font-medium text-gray-800">{objectName}</h3>
        <p className="text-sm text-gray-500">{classInfo.name}</p>

        <div className="mt-4 space-y-2">
          {classInfo.fields.map((fieldInfo, index) => {
            const value = fieldValues[index] ?? fieldInfo.default ?? '';
            if (!value && !fieldInfo.required) return null;

            return (
              <div
                key={index}
                className={cn(
                  'text-sm',
                  isMobile
                    ? 'flex flex-col gap-0.5'
                    : 'flex justify-between'
                )}
              >
                <span className="text-gray-600">
                  {fieldInfo.name || fieldInfo.id}
                </span>
                <span
                  className={cn(
                    'font-medium text-gray-800',
                    isMobile && 'break-all'
                  )}
                >
                  {value || <span className="text-gray-400">(empty)</span>}
                </span>
              </div>
            );
          })}
        </div>

        {classInfo.memo && (
          <div className="mt-4 rounded bg-blue-50 p-3 text-xs text-blue-700">
            {classInfo.memo}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Object Creation Wizard Component
 */
export function ObjectCreationWizard() {
  const { isOpen, step, selectedClass, fieldValues } = useWizardState();
  const isMobile = useIsMobile();

  const schema = useIDDStore((state) => state.schema);
  const addObject = useIDFStore((state) => state.addObject);

  const closeWizard = useVisualObjectEditorStore((state) => state.closeWizard);
  const setWizardStep = useVisualObjectEditorStore((state) => state.setWizardStep);
  const setWizardClass = useVisualObjectEditorStore((state) => state.setWizardClass);
  const setWizardFieldValue = useVisualObjectEditorStore(
    (state) => state.setWizardFieldValue
  );
  const resetWizard = useVisualObjectEditorStore((state) => state.resetWizard);

  // Get class info for selected class
  const classInfo = useMemo(() => {
    if (!selectedClass) return null;
    return schema.objectTypes.get(selectedClass.toLowerCase());
  }, [schema, selectedClass]);

  // Pre-fill defaults when class is selected
  // This also initializes required choice fields to their first choice if no default is specified
  useEffect(() => {
    if (classInfo) {
      classInfo.fields.forEach((field, index) => {
        if (fieldValues[index] === undefined) {
          // Use explicit default if available
          if (field.default !== undefined) {
            setWizardFieldValue(index, field.default);
          }
          // For required choice fields without a default, use the first choice
          // This ensures the displayed value matches the stored value for validation
          else if (field.required && field.choices && field.choices.length > 0) {
            const firstChoice = field.choices[0];
            if (firstChoice !== undefined) {
              setWizardFieldValue(index, firstChoice);
            }
          }
        }
      });
    }
  }, [classInfo, fieldValues, setWizardFieldValue]);

  // Handle class selection
  const handleClassSelect = useCallback(
    (className: string) => {
      setWizardClass(className);
      setWizardStep('required');
    },
    [setWizardClass, setWizardStep]
  );

  // Handle create
  const handleCreate = useCallback(() => {
    if (!classInfo) return;

    // Build fields array
    const fields: IDFField[] = classInfo.fields.map((fieldInfo, index) => {
      const field: IDFField = {
        value: fieldValues[index] ?? fieldInfo.default ?? '',
      };
      if (fieldInfo.name) {
        field.inlineComment = `!- ${fieldInfo.name}`;
      }
      return field;
    });

    // Add object to document
    addObject({
      className: classInfo.name,
      fields,
      leadingComments: [],
    });

    resetWizard();
  }, [classInfo, fieldValues, addObject, resetWizard]);

  // Check if required fields are filled
  const requiredFieldsFilled = useMemo(() => {
    if (!classInfo) return false;
    return classInfo.fields
      .filter((f) => f.required)
      .every((f) => {
        const fieldIndex = classInfo.fields.indexOf(f);
        const value = fieldValues[fieldIndex] ?? f.default ?? '';
        return value.trim() !== '';
      });
  }, [classInfo, fieldValues]);

  // Navigation
  const canGoBack = step !== 'class';
  const canGoNext =
    (step === 'class' && selectedClass) ||
    (step === 'required' && requiredFieldsFilled) ||
    step === 'optional';
  const isLastStep = step === 'review';

  const goBack = () => {
    if (step === 'required') setWizardStep('class');
    else if (step === 'optional') setWizardStep('required');
    else if (step === 'review') setWizardStep('optional');
  };

  const goNext = () => {
    if (step === 'required') setWizardStep('optional');
    else if (step === 'optional') setWizardStep('review');
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex bg-black/50',
        isMobile ? 'items-end' : 'items-center justify-center'
      )}
    >
      <div
        className={cn(
          'flex flex-col bg-white shadow-xl',
          isMobile
            ? 'h-[90dvh] w-full rounded-t-xl'
            : 'h-[80vh] w-full max-w-3xl rounded-lg'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between border-b',
            isMobile ? 'px-4 py-3' : 'px-6 py-4'
          )}
        >
          {/* Mobile drag handle indicator */}
          {isMobile && (
            <div className="absolute left-1/2 top-2 h-1 w-8 -translate-x-1/2 rounded-full bg-gray-300" />
          )}
          <h2
            className={cn(
              'font-semibold text-gray-800',
              isMobile ? 'text-base' : 'text-lg'
            )}
          >
            Create New Object
          </h2>
          <button
            onClick={closeWizard}
            className={cn(
              'rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600',
              isMobile ? 'p-2 -mr-2' : 'p-1'
            )}
          >
            <svg
              className={cn(isMobile ? 'h-5 w-5' : 'h-6 w-6')}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
            </svg>
          </button>
        </div>

        {/* Progress */}
        <div className="border-b">
          <WizardProgress currentStep={step} isMobile={isMobile} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {step === 'class' && (
            <ClassSelectionStep onSelect={handleClassSelect} isMobile={isMobile} />
          )}
          {step === 'required' && classInfo && (
            <FieldsStep
              classInfo={classInfo}
              fieldValues={fieldValues}
              onFieldChange={setWizardFieldValue}
              showRequired={true}
              isMobile={isMobile}
            />
          )}
          {step === 'optional' && classInfo && (
            <FieldsStep
              classInfo={classInfo}
              fieldValues={fieldValues}
              onFieldChange={setWizardFieldValue}
              showRequired={false}
              isMobile={isMobile}
            />
          )}
          {step === 'review' && classInfo && (
            <ReviewStep
              classInfo={classInfo}
              fieldValues={fieldValues}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* Footer - extra bottom padding on mobile for nav bar */}
        <div
          className={cn(
            'flex justify-between border-t',
            isMobile ? 'px-4 pt-3 pb-20' : 'px-6 py-4'
          )}
          style={isMobile ? { paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' } : undefined}
        >
          <button
            onClick={goBack}
            disabled={!canGoBack}
            className={cn(
              'rounded-md border border-gray-300 font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed',
              isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-2 text-sm'
            )}
          >
            Back
          </button>
          <div className="flex gap-2 sm:gap-3">
            {!isMobile && (
              <button
                onClick={closeWizard}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            {isLastStep ? (
              <button
                onClick={handleCreate}
                className={cn(
                  'rounded-md bg-blue-600 font-medium text-white hover:bg-blue-700',
                  isMobile ? 'px-4 py-2.5 text-sm' : 'px-4 py-2 text-sm'
                )}
              >
                Create
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={!canGoNext}
                className={cn(
                  'rounded-md bg-blue-600 font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
                  isMobile ? 'px-4 py-2.5 text-sm' : 'px-4 py-2 text-sm'
                )}
              >
                {step === 'optional' ? 'Review' : 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
